import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Truck, CheckCircle2, XCircle, Trash2 } from 'lucide-react';
import { ordenarPedidos, usePedidosStore } from './store';
import type { Pedido, StatusBackend } from './types';
import { formatTicket, tempoRelativo } from '@/lib/format';
import Money from '@/components/Money';
import Pill, { type PillTone } from '@/components/Pill';
import { cn } from '@/lib/cn';
import { useToast } from '@/components/Toast';

const ROTULOS: Record<StatusBackend, { label: string; tone: PillTone; Icon: typeof Truck }> = {
  recebido: { label: 'Pedido novo', tone: 'amber', Icon: Truck },
  pago: { label: 'Aguardando pagamento', tone: 'moss', Icon: Truck },
  em_separacao: { label: 'Em separação', tone: 'sage', Icon: Truck },
  em_rota: { label: 'Em rota', tone: 'soil', Icon: Truck },
  entregue: { label: 'Entregue', tone: 'mute', Icon: CheckCircle2 },
  cancelado: { label: 'Cancelado', tone: 'petal', Icon: XCircle },
};

const STATUSES_HISTORICO: StatusBackend[] = ['em_rota', 'entregue', 'cancelado'];

export function HistoricoSection() {
  const pedidosMap = usePedidosStore((s) => s.pedidos);
  const selecionar = usePedidosStore((s) => s.selecionar);
  const excluir = usePedidosStore((s) => s.excluir);
  const toast = useToast();
  const [aberto, setAberto] = useState(false);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [excluindoId, setExcluindoId] = useState<number | null>(null);

  // Reseta confirm pending quando seção colapsa
  useEffect(() => {
    if (!aberto) setConfirmId(null);
  }, [aberto]);

  const pedidos: Pedido[] = useMemo(
    () => ordenarPedidos(pedidosMap).filter((p) => STATUSES_HISTORICO.includes(p.status)),
    [pedidosMap],
  );

  if (pedidos.length === 0) return null;

  const contagem = pedidos.reduce<Record<StatusBackend, number>>(
    (acc, p) => {
      acc[p.status] = (acc[p.status] ?? 0) + 1;
      return acc;
    },
    { recebido: 0, pago: 0, em_separacao: 0, em_rota: 0, entregue: 0, cancelado: 0 },
  );

  return (
    <section className="border-t border-[var(--border-subtle)] pt-s5">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="flex items-center gap-s3 w-full text-left group"
        aria-expanded={aberto}
      >
        <span className="t-overline">Histórico</span>
        <span className="font-mono text-[11px] text-fg-3">
          {pedidos.length} {pedidos.length === 1 ? 'pedido' : 'pedidos'}
        </span>
        <div className="flex items-center gap-s2 ml-s4">
          {contagem.em_rota > 0 && (
            <Pill tone="soil" dot={false}>
              {contagem.em_rota} em rota
            </Pill>
          )}
          {contagem.entregue > 0 && (
            <Pill tone="mute" dot={false}>
              {contagem.entregue} entregue{contagem.entregue > 1 ? 's' : ''}
            </Pill>
          )}
          {contagem.cancelado > 0 && (
            <Pill tone="petal" dot={false}>
              {contagem.cancelado} cancelado{contagem.cancelado > 1 ? 's' : ''}
            </Pill>
          )}
        </div>
        <span className="ml-auto text-fg-3 group-hover:text-fg-1 transition-colors duration-fast">
          <ChevronDown
            size={16}
            strokeWidth={1.5}
            className={cn('transition-transform duration-base', aberto && 'rotate-180')}
          />
        </span>
      </button>

      {aberto && (
        <ul className="mt-s5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-s3">
          {pedidos.map((p, idx) => {
            const info = ROTULOS[p.status];
            const Icon = info.Icon;
            const emConfirm = confirmId === p.id;
            const sendoExcluido = excluindoId === p.id;
            return (
              <li
                key={p.id}
                className={cn(
                  'group flex items-center gap-s4 px-s4 py-s3 bg-card border rounded-md transition-all duration-fast',
                  'animate-cardEnter',
                  emConfirm
                    ? 'border-petal-700 bg-[rgba(185,111,108,0.06)]'
                    : 'border-[var(--border)] hover:bg-card-hover hover:border-[var(--border-strong)]',
                  sendoExcluido && 'opacity-50 pointer-events-none',
                )}
                style={{ animationDelay: `${Math.min(idx, 12) * 30}ms` }}
              >
                <button
                  type="button"
                  onClick={() => selecionar(p.id)}
                  className="flex items-center gap-s4 flex-1 min-w-0 text-left"
                >
                  <span className="font-mono text-[11px] text-moss-300">
                    {formatTicket(p.numero_ticket)}
                  </span>
                  <span className="font-body font-semibold text-[13px] text-fg-1 flex-1 truncate">
                    {p.nome_cliente}
                  </span>
                  <Pill tone={info.tone} dot={false}>
                    <Icon size={11} strokeWidth={1.6} />
                    {info.label}
                  </Pill>
                  <Money valor={p.total} size="sm" />
                  <span className="font-mono text-[10px] text-fg-3 min-w-[64px] text-right">
                    {tempoRelativo(p.atualizado_em || p.criado_em)}
                  </span>
                </button>

                {emConfirm ? (
                  <div className="flex items-center gap-s2">
                    <button
                      type="button"
                      disabled={sendoExcluido}
                      onClick={async (e) => {
                        e.stopPropagation();
                        const ticket = p.numero_ticket;
                        setExcluindoId(p.id);
                        try {
                          await excluir(p.id);
                          toast('success', `${ticket} excluído.`);
                        } catch (err) {
                          toast('error', `Falha ao excluir ${ticket} — ${(err as Error).message}.`);
                        } finally {
                          setExcluindoId(null);
                          setConfirmId(null);
                        }
                      }}
                      className="px-s3 py-[6px] rounded-sm border border-petal-500 text-petal-300 text-[11px] font-semibold uppercase tracking-[0.08em] hover:bg-[rgba(185,111,108,0.12)] transition-colors duration-fast disabled:opacity-50"
                    >
                      {sendoExcluido ? 'Excluindo…' : 'Confirmar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmId(null)}
                      className="px-s3 py-[6px] rounded-sm border border-[var(--border)] text-fg-3 text-[11px] font-semibold uppercase tracking-[0.08em] hover:text-fg-1 hover:border-[var(--border-strong)] transition-colors duration-fast"
                    >
                      Voltar
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmId(p.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-fast text-fg-3 hover:text-petal-300 p-s2 rounded-sm border border-transparent hover:border-petal-700"
                    aria-label={`Excluir pedido ${p.numero_ticket}`}
                  >
                    <Trash2 size={14} strokeWidth={1.6} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default HistoricoSection;
